import React, { useState, useEffect, useMemo } from "react";
import {
  FaEdit,
  FaTrash,
  FaFileInvoice,
  FaCalendarCheck,
  FaSearch,
  FaSignOutAlt,
  FaFileContract,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "./admin.css";
import Swal from "sweetalert2";
import {
  create_student,
  unlink_student,
  update_student,
  get_product,
  get_groups,
  create_product,
  get_trainer,
} from "../services/services";
import Loading from "../utils/loading";
import { Login, setData } from "../services/login.service";
import { ModalFacturas } from "./modal_facturas";
import { ModalAsistencias } from "./modal_asistencias";
import { ModalContrato } from "./modal_contrato";

const AdminUsers = () => {
  const usuario = JSON.parse(localStorage.getItem("credenciales"));
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trainer, setTrainer] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const [showOtherProductModal, setShowOtherProductModal] = useState(false);
  const [otherProduct, setOtherProduct] = useState({
    name: "",
    price: "",
  });

  // Agregar la función formatPrice aquí
  const formatPrice = (value) => {
    if (!value) return "";
    // Eliminar todos los caracteres no numéricos
    const numericValue = value.toString().replace(/\D/g, "");
    // Convertir a número
    const number = parseInt(numericValue, 10);
    if (isNaN(number)) return "";
    // Formatear con separadores de miles
    return number.toLocaleString("es-CO");
  };

  const [isContratoModalOpen, setIsContratoModalOpen] = useState(false);
  const [selectedUserForContract, setSelectedUserForContract] = useState(null);
  const [isFacturasModalOpen, setIsFacturasModalOpen] = useState(false);
  const [isAsistenciasModalOpen, setIsAsistenciasModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus] = useState("Todos");

  const initialFormState = {
    id: "",
    name: "",
    vat: "",
    mobile: "",
    identification_type: "Cédula de ciudadanía",
    street: "",
    email: "",
    birthday: "",
    product_id: "",
    price: "",
    category_id: "",
    team_user_id: "",
  };

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const handleProductChange = (e) => {
    const { name, value } = e.target;

    if (value === "Crear productos") {
      setShowOtherProductModal(true);
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,

        // Actualizar el precio del producto cuando se selecciona uno nuevo
        product_price: value
          ? products
              .find((p) => p.id.toString() === value.toString())
              ?.price?.toString() || ""
          : "",
      }));
    }
  };

  // Agregar nuevo manejador para el cambio de precio
  const handlePriceChange = (e) => {
    const { value } = e.target;
    // Eliminar todos los caracteres no numéricos del input
    const numericValue = value.replace(/\D/g, "");

    setFormData((prevState) => ({
      ...prevState,
      product_price: numericValue, // Guardamos el valor sin formato
    }));
  };

  const handleOtherProductChange = (e) => {
    const { name, value } = e.target;
    setOtherProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateOtherProduct = () => {
    if (!otherProduct.name || !otherProduct.price) {
      Swal.fire({
        title: "Error",
        text: "Por favor complete todos los campos del producto",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    const productExists = products.some(
      (product) =>
        product.name.toLowerCase() === otherProduct.name.toLowerCase(),
    );

    if (productExists) {
      Swal.fire({
        title: "Error",
        text: "Ya existe un producto con este nombre. Por favor, elija otro nombre.",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    setLoading(true);

    const dataSend = {
      name: otherProduct.name,
      list_price: Number.parseFloat(otherProduct.price),
    };

    create_product(dataSend, usuario, (success, response) => {
      setLoading(false);

      if (success) {
        const newProductName = otherProduct.name;
        const newProductPrice = otherProduct.price;

        Swal.fire({
          title: "¡Éxito!",
          text: "Producto creado correctamente",
          icon: "success",
          confirmButtonColor: "#000000",
        }).then(() => {
          get_product(
            { user: usuario.user, password: usuario.password },
            (success, response) => {
              if (success && Array.isArray(response)) {
                const formattedProducts = response.map((item) => ({
                  id: item.product_tmpl_id,
                  name: item.nombre,
                  price: item.price,
                }));

                // Agregar opción "Crear productos" al final
                formattedProducts.push({
                  id: "Crear productos",
                  name: "Crear productos",
                  price: null,
                });

                setProducts(formattedProducts);

                // Encontrar el producto recién creado
                const newProduct = formattedProducts.find(
                  (p) => p.name === newProductName,
                );

                if (newProduct) {
                  // Actualizar ambos campos: producto y precio
                  setFormData((prevState) => ({
                    ...prevState,
                    product_id: newProduct.id,
                    product_price: newProduct.price.toString(),
                  }));
                }
              }
            },
          );

          setOtherProduct({ name: "", price: "" });
          setShowOtherProductModal(false);
        });
      } else {
        Swal.fire({
          title: "Error",
          text: "No se pudo crear el producto. Por favor intente nuevamente.",
          icon: "error",
          confirmButtonColor: "#000000",
        });
      }
    });
  };

  const handleInvoiceClick = (user) => {
    setSelectedUserId(user.id);
    setIsFacturasModalOpen(true);
  };

  const handleAttendanceClick = (user) => {
    setSelectedUserId(user.id);
    setIsAsistenciasModalOpen(true);
  };

  const handleContratoClick = (user) => {
    setSelectedUserForContract(user);
    setIsContratoModalOpen(true);
  };

  const handleLogout = () => {
    Swal.fire({
      title: "¿Estás seguro de cerrar sesión?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#000000",
      cancelButtonColor: "#807e80",
      confirmButtonText: "Sí, cerrar sesión",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("credenciales");
        localStorage.removeItem("students");
        window.location.href = "/";
      }
    });
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.vat?.toString().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        filterStatus === "Todos" || user.estado === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [users, searchTerm, filterStatus]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        get_groups(
          { user: usuario.user, password: usuario.password },
          (success, response) => {
            if (success && Array.isArray(response)) {
              setCategories(
                response.map((category) => ({
                  id: category.id,
                  name: category.name,
                })),
              );
            } else {
              console.log("Error al obtener las categorías:", response);
            }
          },
        );

        await new Promise((resolve) => setTimeout(resolve, 2000));

        get_product(
          { user: usuario.user, password: usuario.password },
          (success, response) => {
            if (success && Array.isArray(response)) {
              const formattedProducts = response.map((item) => ({
                id: item.product_tmpl_id,
                name: item.nombre,
                price: item.price,
              }));

              formattedProducts.push({
                id: "Crear productos",
                name: "Crear productos",
                price: null,
              });

              setProducts(formattedProducts);
            } else {
              console.log("Error en la respuesta:", response);
            }
          },
        );

        get_trainer(
          { user: usuario.user, password: usuario.password },
          (success, response) => {
            if (success) {
              setTrainer(JSON.parse(response));
            } else {
              console.log("Error al obtener los entrenadores:", response);
            }
          },
        );

        await Login(
          { user: usuario.user, password: usuario.password },
          (response) => {
            const result = JSON.parse(localStorage.getItem("students"));
            if (response) {
              setUsers(result.student || []);
            } else {
              console.error("Error al cargar los datos:", response);
            }
            setLoading(false);
          },
        );
      } catch (error) {
        console.error("Error en la carga de datos:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [usuario.user, usuario.password]);

  const handleCreateUser = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setErrorMessage("");
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        title: "Error",
        text: "Por favor ingrese un correo electrónico válido",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    if (!/^\d+$/.test(formData.mobile)) {
      Swal.fire({
        title: "Error",
        text: "El número de teléfono debe contener solo dígitos sin espacios",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    if (!formData.product_id || formData.product_id === "Crear productos") {
      Swal.fire({
        title: "Error",
        text: "Por favor seleccione un producto",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    if (!formData.category_id) {
      Swal.fire({
        title: "Error",
        text: "Por favor seleccione una categoría",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    setLoading(true);

    const sanitizedFormData = {
      name: formData.name || "",
      vat: formData.vat || "",
      email: formData.email || "",
      mobile: formData.mobile || "",
      identification_type:
        formData.identification_type || "Cédula de ciudadanía",
      birthday: formData.birthday || "",
      street: formData.street || "",
      product_id: Number.parseInt(formData.product_id),
      // Convertir el precio a número y eliminar cualquier formato
      price: Number(formData.product_price.replace(/[.,\s]/g, "")),
      category_id: Number.parseInt(formData.category_id),
      team_user_id: Number.parseInt(formData.team_user_id),
    };

    if (isEditing) {
      sanitizedFormData.id = formData.id;

      // Agregar este console.log para debug
      console.log("Datos a actualizar:", sanitizedFormData);

      update_student(sanitizedFormData, usuario, (success, response) => {
        setLoading(false);
        if (success) {
          Swal.fire({
            title: "¡Éxito!",
            text: "Deportista actualizado correctamente",
            icon: "success",
            confirmButtonColor: "#000000",
          }).then(() => {
            window.location.reload();
          });
        } else {
          if (
            response.message &&
            response.message.includes("res_users_login_key")
          ) {
            setErrorMessage(
              "El correo ya se encuentra registrado. Por favor, ingrese un correo diferente.",
            );
          } else {
            Swal.fire({
              title: "Error",
              text:
                response.message ||
                "No se pudo actualizar el deportista. Por favor, intente nuevamente.",
              icon: "error",
              confirmButtonColor: "#000000",
            });
          }
        }
      });
    } else {
      create_student(sanitizedFormData, usuario, (success, response) => {
        setLoading(false);
        if (success) {
          Swal.fire({
            title: "¡Éxito!",
            text: "Deportista creado correctamente",
            icon: "success",
            confirmButtonColor: "#000000",
          }).then(() => {
            window.location.reload();
          });
        } else {
          if (
            response.message &&
            response.message.includes("res_users_login_key")
          ) {
            setErrorMessage(
              "El correo ya se encuentra registrado. Por favor, ingrese un correo diferente.",
            );
          } else if (
            response.message &&
            response.message.includes("Free Test (apichat.io) is not connected")
          ) {
            Swal.fire({
              title: "Información",
              text: "El deportista fue creado exitosamente, pero no se logró hacer envío de información a WhatsApp.",
              icon: "info",
              confirmButtonColor: "#000000",
            }).then(() => {
              window.location.reload();
            });
          } else {
            Swal.fire({
              title: "Error",
              text:
                response.message ||
                "No se pudo crear el deportista. Por favor, intente nuevamente.",
              icon: "error",
              confirmButtonColor: "#000000",
            });
          }
        }
      });
    }
  };

  const handleEdit = (userId) => {
    const userToEdit = users.find((user) => user.id === userId);

    if (!userToEdit) {
      console.error("Usuario no encontrado con ID:", userId);
      return;
    }

    console.log("Usuario a editar:", userToEdit); // Debug
    console.log("Precio del usuario:", userToEdit.price); // Debug

    const formDataToSet = {
      ...userToEdit,
      product_id: userToEdit.product_id?.[0]?.id || "",
      product_price: userToEdit.price?.toString() || "",
      category_id: userToEdit.category_id?.[0]?.id || "",
      team_user_id: userToEdit.team_user_id?.[0]?.id || "",
    };

    console.log("Datos del formulario:", formDataToSet); // Debug

    setFormData(formDataToSet);
    setIsEditing(true);
    setEditIndex(users.findIndex((user) => user.id === userId));
    setErrorMessage("");
    setShowModal(true);
  };

  const handleDelete = (user) => {
    if (!user) {
      Swal.fire({
        title: "Error",
        text: "Usuario no encontrado",
        icon: "error",
        confirmButtonColor: "#000000",
      });
      return;
    }

    Swal.fire({
      title: `¿Estás seguro de eliminar a ${user.name} con ${user.identification_type} : ${user.vat} ?`,
      text: `Esta acción no se puede deshacer y eliminará permanentemente el registro.  `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#000000",
      cancelButtonColor: "#807e80",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);

        const userToDelete = {
          id: user.id,
          product_id: user.product_id,
          category_id: user.category_id,
        };

        unlink_student(userToDelete, usuario, (flag, res) => {
          setLoading(false);

          if (flag) {
            Swal.fire({
              title: "Eliminado",
              text: `El deportista ${user.name} ha sido eliminado exitosamente.`,
              icon: "success",
              confirmButtonColor: "#000000",
            }).then(() => {
              Login(
                { user: usuario.user, password: usuario.password },
                (response) => {
                  if (response) {
                    const result = JSON.parse(localStorage.getItem("students"));
                    setUsers(result.student || []);
                  }
                },
              );
            });
          } else {
            Swal.fire({
              title: "Error",
              text: `No se pudo eliminar al deportista ${user.name}. Por favor, intente nuevamente.`,
              icon: "error",
              confirmButtonColor: "#000000",
            });
            console.error("Error en la eliminación:", res);
          }
        });
      }
    });
  };

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredUsers.length / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="admin-users-page">
      {showOtherProductModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div
            className="modal fade show"
            tabIndex="-1"
            role="dialog"
            style={{ display: "block" }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content product-modal-content">
                <div className="modal-header text-white rounded-top product-modal-header">
                  <h5 className="modal-title product-modal-title">
                    Nuevo Producto
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowOtherProductModal(false);
                      setFormData((prev) => ({ ...prev, product_id: "" }));
                    }}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body product-modal-body">
                  <div className="col-md-6 mb-3 product-name-field">
                    <label
                      htmlFor="otherProductName"
                      className="form-label product-form-label"
                    >
                      Nombre del Producto
                    </label>
                    <input
                      type="text"
                      className="form-control product-input gradient-border-input"
                      id="otherProductName"
                      name="name"
                      value={otherProduct.name}
                      onChange={handleOtherProductChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3 product-price-field">
                    <label
                      htmlFor="otherProductPrice"
                      className="form-label product-form-label"
                    >
                      Valor
                    </label>

                    <div className="input-container">
                      <span
                        className="currency-symbol"
                        style={{ color: "#ffffff" }}
                      >
                        $
                      </span>
                      <input
                        type="text"
                        className="form-control product-input gradient-border-input"
                        id="otherProductName"
                        name="price"
                        value={otherProduct.price}
                        onChange={handleOtherProductChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer product-modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary product-cancel-btn"
                    onClick={() => {
                      setShowOtherProductModal(false);
                      setFormData((prev) => ({ ...prev, product_id: "" }));
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary product-create-btn"
                    onClick={handleCreateOtherProduct}
                    style={{
                      backgroundColor: "#000000",
                      borderColor: "#000000",
                    }}
                  >
                    Crear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className="logout-container"
        style={{
          position: "absolute",
          top: "22px",
          right: "109px",
          zIndex: 1000,
        }}
      >
        <button
          className="logout-btn"
          onClick={handleLogout}
          title="Cerrar Sesión"
        >
          <FaSignOutAlt /> Salir
        </button>
      </div>

      <div className="bg-image"></div>
      <div className="content-wrapper">
        <h1 className="tittle">Administrar deportistas</h1>

        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2">
            <button
              className={`create-user-button ${
                showModal ? "hide-on-mobile" : ""
              }`}
              onClick={handleCreateUser}
            >
              Crear deportista
            </button>

            <button
              className="create-user-button2"
              onClick={() => setIsContratoModalOpen(true)}
            >
              <FaFileContract style={{ marginRight: 6 }} />
              Contrato
            </button>
          </div>

          <div className="d-flex">
            <div className="input-group me-2 buscador">
              <span className="input-group-text ">
                <FaSearch />
              </span>
              <input
                type="text"
                className="buscar form-control"
                placeholder="Buscar por nombre, documento o email"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="users-table">
            <thead style={{ backgroundColor: "#6c757d", color: "white" }}>
              <tr>
                <th>#</th>
                <th className="nombre_mobile">Nombre completo</th>
                <th className="hide-mobile">Tipo Documento</th>
                <th className="hide-mobile">Documento</th>
                <th className="hide-mobile">Fecha nacimiento</th>
                <th>Telefono</th>
                <th>Email</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user, index) => (
                <tr key={user.id || index}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{user.name}</td>
                  <td className="hide-mobile">{user.identification_type}</td>
                  <td className="hide-mobile">{user.vat}</td>
                  <td className="hide-mobile">{user.birthday}</td>
                  <td>{user.mobile}</td>
                  <td>{user.email}</td>
                  <td>
                    <div className="action-icons">
                      <FaEdit
                        className="edit-icon"
                        onClick={() => handleEdit(user.id)}
                        title="Editar"
                      />
                      <FaTrash
                        className="delete-icon"
                        onClick={() => handleDelete(user)}
                        title="Eliminar"
                      />
                      <FaFileInvoice
                        className="invoice-icon"
                        onClick={() => handleInvoiceClick(user)}
                        title="Factura"
                      />
                      <FaCalendarCheck
                        className="attendance-icon"
                        onClick={() => handleAttendanceClick(user)}
                        title="Asistencia"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={`pagination-container ${
            showModal ? "hide-on-mobile" : ""
          }`}
        >
          <div style={{ color: "#ffffff" }}>
            Mostrando {indexOfFirstItem + 1} -{" "}
            {Math.min(indexOfLastItem, filteredUsers.length)} de{" "}
            {filteredUsers.length} resultados
          </div>
          <ul className="pagination" style={{ marginLeft1: "1%" }}>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
            </li>
            {pageNumbers.map((number) => (
              <li
                key={number}
                className={`page-item ${
                  currentPage === number ? "active" : ""
                }`}
              >
                <button
                  onClick={() => paginate(number)}
                  className="page-link"
                  style={{
                    color: currentPage === number ? "black" : "",
                  }}
                >
                  {number}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === pageNumbers.length ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === pageNumbers.length}
              >
                Siguiente
              </button>
            </li>
          </ul>
        </div>
        <Loading isVisible={loading} />
        {showModal && (
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header text-white rounded-top">
                  <h5 className="modal-title">
                    {isEditing ? "Editar Deportista" : "Crear Nuevo Deportista"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowModal(false);
                      setErrorMessage("");
                    }}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {errorMessage && (
                      <div className="alert alert-danger" role="alert">
                        {errorMessage}
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="nombre" className="form-label">
                          Nombre Completo
                        </label>
                        <input
                          type="text"
                          className="form-control gradient-border-input letra"
                          id="nombre"
                          name="name"
                          placeholder="Ingrese nombre completo"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="tipo_documento" className="form-label">
                          Tipo Documento
                        </label>
                        <select
                          className="form-select gradient-border-input"
                          id="tipo_documento"
                          name="identification_type"
                          value={formData.identification_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="Cédula de ciudadanía">
                            Cédula de Ciudadanía
                          </option>
                          <option value="Tarjeta de Identidad">
                            Tarjeta de Identidad
                          </option>
                          <option value="Registro Civil">Registro Civil</option>
                          <option value="Tarjeta de extranjería">
                            Tarjeta de extranjería
                          </option>
                          <option value="Cédula de extranjería">
                            Cédula extranjería
                          </option>
                          <option value="Pasaporte">Pasaporte</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="documento" className="form-label">
                          Número Documento
                        </label>
                        <input
                          type="text"
                          className="form-control gradient-border-input"
                          id="documento"
                          name="vat"
                          placeholder="Ingrese número de documento"
                          value={formData.vat}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label
                          htmlFor="fecha_nacimiento"
                          className="form-label"
                        >
                          Fecha Nacimiento
                        </label>
                        <input
                          type="date"
                          className="form-control gradient-border-input"
                          id="fecha_nacimiento"
                          name="birthday"
                          value={formData.birthday}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="telefono" className="form-label">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          className="form-control gradient-border-input"
                          id="telefono"
                          name="mobile"
                          placeholder="Ingrese número de teléfono"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="correo" className="form-label">
                          Correo Electrónico
                        </label>
                        <input
                          type="email"
                          className="form-control gradient-border-input"
                          id="correo"
                          name="email"
                          placeholder="Ingrese correo electrónico"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="product" className="form-label">
                          Producto
                        </label>
                        <select
                          className="form-select gradient-border-input"
                          id="product"
                          name="product_id"
                          value={formData.product_id}
                          onChange={handleProductChange}
                          required
                        >
                          <option value="">Seleccione un producto</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.id === "Crear productos"
                                ? product.name
                                : `${product.name} - $${
                                    product.price
                                      ? product.price.toLocaleString("es-CO")
                                      : "0"
                                  }`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="product_price" className="form-label">
                          Valor Producto
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="text"
                            className="form-control gradient-border-input"
                            id="product_price"
                            name="product_price"
                            value={
                              formData.product_price
                                ? formatPrice(formData.product_price)
                                : ""
                            } // Formatear solo para visualización
                            onChange={handlePriceChange}
                            placeholder="Valor del producto"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="category" className="form-label">
                          Categoría
                        </label>
                        <select
                          className="form-select gradient-border-input"
                          id="category"
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Seleccione una categoría</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="trainer" className="form-label">
                          Entrenador
                        </label>
                        <select
                          className="form-select gradient-border-input"
                          id="trainer"
                          name="team_user_id"
                          value={formData.team_user_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Seleccione un entrenador</option>
                          {trainer.map((trn) => (
                            <option key={trn.id} value={trn.id}>
                              {trn.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowModal(false);
                        setErrorMessage("");
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#000000",
                        borderColor: "#000000",
                      }}
                    >
                      {isEditing ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      <ModalAsistencias
        isOpen={isAsistenciasModalOpen}
        onClose={() => setIsAsistenciasModalOpen(false)}
        userId={selectedUserId}
      />
      <ModalFacturas
        isOpen={isFacturasModalOpen}
        onClose={(flag) => {
          setIsFacturasModalOpen(flag);
        }}
        userId={selectedUserId}
      />
      <ModalContrato
        isOpen={isContratoModalOpen}
        onClose={(flag) => setIsContratoModalOpen(flag)}
        userId={selectedUserForContract?.id}
        userName={selectedUserForContract?.name}
      />
    </div>
  );
};

export default AdminUsers;
